import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Wrapper, ValueContainer, SliderContainer, RangeLegend, DoubleInputContainer, DoubleSliderThumb, DoubleSliderTrackContainer, DoubleSliderRange } from './style.js';
import debounce from '../../utils/debounce.js';
import texts from './Slider.texts.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import useGlobals from '../../hooks/useGlobals.js';

var _excluded = ["uniqueId", "className", "min", "max", "format", "valueLabel", "filterSliderToLabel", "step", "hidden", "onChangeComplete", "lowValue", "highValue", "separationLimit"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var formatValue = function formatValue(value, format, siteId) {
  return format ? format(value, siteId) : value;
};
var Doubleslider = function Doubleslider(_ref) {
  var _texts$culture, _texts$culture2;
  var uniqueId = _ref.uniqueId;
    _ref.className;
    var min = _ref.min,
    max = _ref.max,
    format = _ref.format;
    _ref.valueLabel;
    var filterSliderToLabel = _ref.filterSliderToLabel,
    step = _ref.step;
    _ref.hidden;
    var onChangeComplete = _ref.onChangeComplete,
    lowValue = _ref.lowValue,
    highValue = _ref.highValue;
    _ref.separationLimit;
    var rest = _objectWithoutProperties(_ref, _excluded);
  var _useState = useState(lowValue),
    _useState2 = _slicedToArray(_useState, 2),
    localLowValue = _useState2[0],
    setLowValue = _useState2[1];
  var _useState3 = useState(highValue),
    _useState4 = _slicedToArray(_useState3, 2),
    localHighValue = _useState4[0],
    setHighValue = _useState4[1];
  var _useState5 = useState({
      low: 0,
      high: 100
    }),
    _useState6 = _slicedToArray(_useState5, 2),
    percent = _useState6[0],
    setPercent = _useState6[1];
  var _useState7 = useState('low'),
    _useState8 = _slicedToArray(_useState7, 2),
    activeThumb = _useState8[0],
    setActiveThumb = _useState8[1];
  var _useState9 = useState(false),
    _useState10 = _slicedToArray(_useState9, 2),
    hovered = _useState10[0],
    setHovered = _useState10[1];
  var _useState11 = useState(false),
    _useState12 = _slicedToArray(_useState11, 2),
    userInteracted = _useState12[0],
    setUserInteracted = _useState12[1];
  useEffect(function () {
    setLowValue(lowValue);
    setHighValue(highValue);
  }, [lowValue, highValue]);
  var lowValueRef = useRef(null);
  var highValueRef = useRef(null);
  var range = useRef(null);
  var _useGlobals = useGlobals(),
    siteId = _useGlobals.siteId,
    culture = _useGlobals.culture;
  var debouncedOnChangeComplete = useRef(debounce(onChangeComplete, 500, {
    leading: false,
    trailing: true
  }));
  var getPercent = useCallback(function (value) {
    return (value - min) / (max - min) * 100;
  }, [min, max]);
  var handleMinChange = function handleMinChange(e) {
    setUserInteracted(true);
    var value = Math.min(+e.target.value, localHighValue - step);
    setLowValue(value);
  };
  var handleMaxChange = function handleMaxChange(e) {
    setUserInteracted(true);
    var value = Math.max(+e.target.value, localLowValue + step);
    setHighValue(value);
  };
  useEffect(function () {
    if (userInteracted) {
      debouncedOnChangeComplete.current({
        lowValue: localLowValue,
        highValue: localHighValue
      });
    }
  }, [localLowValue, localHighValue, debouncedOnChangeComplete, userInteracted]);
  useEffect(function () {
    if (highValueRef.current) {
      setPercent({
        low: getPercent(localLowValue),
        high: getPercent(+highValueRef.current.value)
      });
    }
    if (lowValueRef.current) {
      setPercent({
        low: getPercent(+lowValueRef.current.value),
        high: getPercent(localHighValue)
      });
    }
  }, [localHighValue, getPercent, percent.high, percent.low, localLowValue]);
  var fromLabel = ((_texts$culture = texts[culture]) === null || _texts$culture === void 0 ? void 0 : _texts$culture.from) || '';
  var toLabel = ((_texts$culture2 = texts[culture]) === null || _texts$culture2 === void 0 ? void 0 : _texts$culture2.to) || '';
  var formattedLowValue = useMemo(function () {
    return formatValue(localLowValue, format, siteId);
  }, [localLowValue, format, siteId]);
  var formattedHighValue = useMemo(function () {
    return formatValue(localHighValue, format, siteId);
  }, [localHighValue, siteId, format]);
  var valueText = filterSliderToLabel ? "".concat(formattedLowValue, " ").concat(filterSliderToLabel, " ").concat(formattedHighValue) : "".concat(formattedLowValue, " ").concat(formattedHighValue);
  var handleMouseMove = function handleMouseMove(e) {
    var rangeRect = range.current.getBoundingClientRect();
    var x = e.clientX - rangeRect.left;
    var percentage = Math.round(x / rangeRect.width * 100);
    var lowValuePercent = getPercent(localLowValue);
    var highValuePercent = getPercent(localHighValue);
    if (Math.abs(percentage - lowValuePercent) < Math.abs(percentage - highValuePercent)) {
      setActiveThumb('low');
    } else {
      setActiveThumb('high');
    }
  };
  var handleTouchMove = function handleTouchMove(e) {
    setUserInteracted(true);
    if (!range.current) return;
    var rangeRect = range.current.getBoundingClientRect();
    var x = e.touches[0].clientX - rangeRect.left;
    var percentage = Math.round(x / rangeRect.width * 100);
    var lowValuePercent = getPercent(localLowValue);
    var highValuePercent = getPercent(localHighValue);
    var calculateValue = function calculateValue(perc) {
      var value = perc / 100 * (max - min) + min;
      var roundedValue = Math.round(value / step) * step;
      return parseFloat(roundedValue.toFixed(1));
    };
    var buffer = step / 2;
    if (Math.abs(percentage - lowValuePercent) < Math.abs(percentage - highValuePercent)) {
      var newLowValue = calculateValue(percentage);
      if (newLowValue >= localHighValue - buffer || newLowValue < min) return;
      setLowValue(newLowValue);
      setPercent({
        low: percentage,
        high: highValuePercent
      });
    } else {
      var newHighValue = calculateValue(percentage);
      if (newHighValue <= localLowValue + buffer || newHighValue > max) return;
      setHighValue(newHighValue);
      setPercent({
        low: lowValuePercent,
        high: percentage
      });
    }
  };
  return /*#__PURE__*/jsxs(Wrapper, _objectSpread(_objectSpread({
    id: uniqueId,
    onMouseLeave: function onMouseLeave() {
      return setHovered(false);
    },
    onMouseEnter: function onMouseEnter() {
      return setHovered(true);
    }
  }, rest), {}, {
    children: [/*#__PURE__*/jsx(ValueContainer, {
      children: /*#__PURE__*/jsx("p", {
        children: valueText
      })
    }), /*#__PURE__*/jsxs(SliderContainer, {
      children: [/*#__PURE__*/jsxs(RangeLegend, {
        children: [/*#__PURE__*/jsxs("div", {
          children: [/*#__PURE__*/jsxs("span", {
            className: "sr-only",
            children: [fromLabel, ":\xA0"]
          }), formatValue(min, format, siteId)]
        }), /*#__PURE__*/jsxs("div", {
          children: [/*#__PURE__*/jsxs("span", {
            className: "sr-only",
            children: [toLabel, ":\xA0"]
          }), formatValue(max, format, siteId)]
        })]
      }), /*#__PURE__*/jsxs(DoubleInputContainer, {
        $activeThumb: activeThumb,
        onMouseMove: handleMouseMove,
        onTouchMove: handleTouchMove,
        onTouchStart: handleTouchMove,
        children: [/*#__PURE__*/jsx(DoubleSliderThumb, {
          value: localLowValue,
          min: min,
          max: max,
          className: "thumb thumb__low",
          onChange: handleMinChange,
          ref: lowValueRef,
          step: step
        }), /*#__PURE__*/jsx(DoubleSliderThumb, {
          value: localHighValue,
          min: min,
          max: max,
          className: "thumb thumb__high",
          onChange: handleMaxChange,
          ref: highValueRef,
          step: step
        }), /*#__PURE__*/jsx(DoubleSliderTrackContainer, {
          $percent: percent,
          children: /*#__PURE__*/jsx(DoubleSliderRange, {
            className: hovered ? 'hovered' : '',
            ref: range,
            $percent: percent,
            $hovered: hovered
          })
        })]
      })]
    })]
  }));
};
Doubleslider.defaultProps = {
  min: 0,
  max: 100,
  value: 0,
  lowValue: 0,
  highValue: 100,
  step: 1,
  onChangeComplete: function onChangeComplete() {},
  format: function format() {},
  valueLabel: '',
  uniqueId: '',
  filterSliderToLabel: null,
  className: undefined,
  hidden: false,
  separationLimit: null
};
var DoubleSlider = Doubleslider;

export { DoubleSlider as default };
//# sourceMappingURL=_DoubleSlider.js.map
